import { useState } from "react";
import { FiChevronDown } from "react-icons/fi";

const faqItems = [
  {
    id: 1,
    question: "How do I buy a product?",
    answer:
      "Add the product to your cart, proceed to checkout, complete payment, and then access your purchase from your account.",
  },
  {
    id: 2,
    question: "Can I download purchased products more than once?",
    answer:
      "Yes, while licenses remain available for the files associated with that product.",
  },
  {
    id: 3,
    question: "How can I become a creator?",
    answer:
      "Open your account menu and choose Become a Creator, then complete the required setup and payment flow.",
  },
  {
    id: 4,
    question: "How do I contact support?",
    answer:
      "Email contact@practidex.com and include your account email plus any order details that can help us identify the issue quickly.",
  },
  {
    id: 5,
    question: "How do licenses work?",
    answer:
      "Each purchased product grants a number of download licenses. Every time you download a file, one remaining license is used for that file. You can always check the remaining count in Purchase History.",
  },
  {
    id: 6,
    question: "How do multi-file products work?",
    answer:
      "Some resources include more than one downloadable file. Each file is listed separately after purchase, and each file keeps its own remaining license count.",
  },
  {
    id: 7,
    question: "What happens if my cart has free and paid items together?",
    answer:
      "Only the paid portion is charged at checkout. Free items in the same cart are granted only after the paid checkout succeeds.",
  },
  {
    id: 8,
    question: "How do free products work after checkout?",
    answer:
      "If an order contains only free products, payment is skipped and you can claim the order directly. Once claimed, the resource appears in your purchase history like any other owned product.",
  },
  {
    id: 9,
    question: "What happens when I follow a creator?",
    answer:
      "Following a creator lets you keep track of their activity more easily from your account, including updates when their visible catalog changes.",
  },
  {
    id: 10,
    question: "How do analytics and tracking preferences work?",
    answer:
      "Practidex keeps non-essential analytics off until you allow them from the tracking preferences banner or footer control. If enabled, Google Analytics can record page visits and marketplace events. Heatmap/session-insight tooling stays off unless it is explicitly enabled for launch and you have already granted analytics consent.",
  },
];

function HelpScreen() {
  const [openIds, setOpenIds] = useState([]);

  const toggleItem = (id) => {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-2xl font-semibold text-slate-900">Help & FAQ</h2>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-[1.1fr_0.9fr] md:items-start">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
              Support
            </p>
            <h3 className="text-xl font-semibold text-slate-900">Need help with your account or order?</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Contact the Practidex team directly at{" "}
              <a
                href="mailto:contact@practidex.com"
                className="font-semibold text-sky-700 no-underline hover:text-sky-800"
              >
                contact@practidex.com
              </a>
              .
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
            Include your account email, order id, and a short description of the problem whenever
            possible. That helps us verify the issue and respond faster.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
        {faqItems.map((item) => {
          const isOpen = openIds.includes(item.id);
          return (
            <article
              key={item.id}
              className="self-start rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md"
            >
              <button
                type="button"
                className="w-full text-left"
                onClick={() => toggleItem(item.id)}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${item.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-semibold text-slate-900">{item.question}</h3>
                  <FiChevronDown
                    className={`shrink-0 text-slate-500 transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>
              <div
                id={`faq-answer-${item.id}`}
                className={`grid transition-all duration-300 ease-out ${
                  isOpen ? "mt-2 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <p className="text-sm leading-6 text-slate-600">{item.answer}</p>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default HelpScreen;
